const desktopMedia = window.matchMedia("(min-width: 768px)");
const initializedSections = new WeakSet();
const initializedSection4MobileSections = new WeakSet();
const section4BackgroundPreloads = new WeakMap();
const initializedSection7s = new WeakSet();
const pendingSection7s = new WeakSet();

const getDirectContainers = (element) =>
  element
    ? Array.from(element.children).filter(
        (child) => child.getAttribute("handle")?.toLowerCase() === "container",
      )
    : [];

const getDirectChildByHandle = (element, handle) =>
  element
    ? Array.from(element.children).find(
        (child) => child.getAttribute("handle")?.toLowerCase() === handle,
      )
    : null;

const section4HotspotPositions = [
  { left: "58%", top: "12.7%" },
  { left: "61.3%", top: "29.9%" },
  { left: "60.9%", top: "41.2%" },
  { left: "60.9%", top: "62.9%" },
  { left: "60.9%", top: "83.6%" },
];

function initializeSection4Hotspots(section, stage, items, onSelect) {
  if (!stage || !items.length) {
    return { setActive: () => {}, animateFor: () => {} };
  }

  const stageImage = stage === section ? null : stage.querySelector("img");
  const hotspotLayer = document.createElement("div");
  hotspotLayer.className = "wide-section4-hotspots";
  hotspotLayer.setAttribute("aria-label", "Section 4 image details");

  const connectorLayer = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "svg",
  );
  connectorLayer.classList.add("wide-section4-connectors");
  connectorLayer.setAttribute("aria-hidden", "true");
  connectorLayer.setAttribute("preserveAspectRatio", "none");

  const buttons = items.map((item, index) => {
    const position =
      section4HotspotPositions[index] ??
      section4HotspotPositions[section4HotspotPositions.length - 1];
    const button = document.createElement("button");
    button.type = "button";
    button.className = "wide-section4-hotspot";
    button.style.left = position.left;
    button.style.top = position.top;
    button.setAttribute("aria-label", `View section 4 item ${index + 1}`);
    button.addEventListener("mouseenter", () => onSelect(index));
    button.addEventListener("focusin", () => onSelect(index));
    button.addEventListener("click", () => onSelect(index));
    hotspotLayer.appendChild(button);

    const line = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "polyline",
    );
    line.classList.add("wide-section4-connector");
    connectorLayer.appendChild(line);
    return { button, item, line };
  });

  stage.appendChild(hotspotLayer);
  section.appendChild(connectorLayer);

  let activeConnectorIndex = 0;
  const updateConnectors = () => {
    if (!section.isConnected) return;

    const sectionRect = section.getBoundingClientRect();
    if (!sectionRect.width || !sectionRect.height) return;

    if (stageImage) {
      const stageRect = stage.getBoundingClientRect();
      const imageRect = stageImage.getBoundingClientRect();
      hotspotLayer.style.left = `${imageRect.left - stageRect.left}px`;
      hotspotLayer.style.top = `${imageRect.top - stageRect.top}px`;
      hotspotLayer.style.width = `${imageRect.width}px`;
      hotspotLayer.style.height = `${imageRect.height}px`;
    }

    connectorLayer.setAttribute(
      "viewBox",
      `0 0 ${sectionRect.width} ${sectionRect.height}`,
    );

    const activeConnector = buttons[activeConnectorIndex];
    if (activeConnector) {
      const { button, item, line } = activeConnector;
      const hotspotRect = button.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const startX = hotspotRect.left + hotspotRect.width / 2 - sectionRect.left;
      const startY = hotspotRect.top + hotspotRect.height / 2 - sectionRect.top;
      const endX = itemRect.left - sectionRect.left;
      const endY = itemRect.top + itemRect.height / 2 - sectionRect.top;
      const bendX = Math.min(endX, startX + Math.abs(endY - startY) / 2);
      line.setAttribute(
        "points",
        `${startX},${startY} ${bendX},${endY} ${endX},${endY}`,
      );
    }
  };

  let connectorFrame = null;
  let connectorTrackingFrame = null;
  let connectorTrackingUntil = 0;
  const scheduleUpdate = () => {
    if (connectorFrame !== null) return;
    connectorFrame = window.requestAnimationFrame(() => {
      connectorFrame = null;
      updateConnectors();
    });
  };
  const resizeObserver =
    typeof ResizeObserver === "function"
      ? new ResizeObserver(scheduleUpdate)
      : null;

  resizeObserver?.observe(section);
  resizeObserver?.observe(stage);
  if (stageImage) resizeObserver?.observe(stageImage);
  items.forEach((item) => resizeObserver?.observe(item));
  window.addEventListener("resize", scheduleUpdate, { passive: true });
  stage.querySelectorAll("img").forEach((image) => {
    image.addEventListener("load", scheduleUpdate, { once: true });
  });
  scheduleUpdate();

  return {
    setActive(activeIndex) {
      activeConnectorIndex = activeIndex;
      buttons.forEach(({ button, item, line }, index) => {
        const isActive = index === activeIndex;
        button.classList.toggle("is-active", isActive);
        line.classList.toggle("is-active", isActive);
        item.classList.toggle("has-hotspot-active", isActive);
      });
      scheduleUpdate();
    },
    animateFor(duration) {
      connectorTrackingUntil = Math.max(
        connectorTrackingUntil,
        performance.now() + duration,
      );
      if (connectorTrackingFrame !== null) return;

      const trackConnector = (timestamp) => {
        connectorTrackingFrame = null;
        updateConnectors();
        if (timestamp < connectorTrackingUntil && section.isConnected) {
          connectorTrackingFrame = window.requestAnimationFrame(trackConnector);
        }
      };

      connectorTrackingFrame = window.requestAnimationFrame(trackConnector);
    },
  };
}

function initializeSection4Mobile(section) {
  if (initializedSection4MobileSections.has(section)) return;

  const mobileStage = getDirectChildByHandle(section, "container1");
  const buttonContainer = getDirectChildByHandle(
    mobileStage,
    "container-button",
  );
  const imageContainer = getDirectChildByHandle(
    mobileStage,
    "container-image",
  );

  if (!mobileStage || !buttonContainer || !imageContainer) return;

  const buttons = Array.from(buttonContainer.children).filter(
    (child) =>
      child.getAttribute("handle")?.toLowerCase() === "related-button",
  );
  const images = Array.from(imageContainer.children).filter(
    (child) => child.getAttribute("handle")?.toLowerCase() === "image",
  );
  const pairCount = Math.min(buttons.length, images.length);
  if (!pairCount) return;

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "wide-section4-mobile-close";
  closeButton.setAttribute("aria-label", "Close image");
  imageContainer.appendChild(closeButton);

  let activeIndex = null;
  let activeButton = null;

  const closeImage = (restoreFocus = true) => {
    imageContainer.classList.remove("is-open");
    imageContainer.setAttribute("aria-hidden", "true");
    buttonContainer.inert = false;
    buttonContainer.removeAttribute("aria-hidden");

    images.forEach((image) => {
      image.classList.remove("is-active");
      image.hidden = true;
      image.setAttribute("aria-hidden", "true");
    });
    buttons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
      button.removeAttribute("data-section4-mobile-active");
    });

    const buttonToFocus = activeButton;
    activeIndex = null;
    activeButton = null;
    delete section.dataset.wd031Section4MobileActive;

    if (restoreFocus && buttonToFocus && !desktopMedia.matches) {
      buttonToFocus.focus({ preventScroll: true });
    }
  };

  const openImage = (index) => {
    if (desktopMedia.matches || index < 0 || index >= pairCount) return;

    activeIndex = index;
    activeButton = buttons[index];
    imageContainer.classList.add("is-open");
    imageContainer.setAttribute("aria-hidden", "false");
    buttonContainer.inert = true;
    buttonContainer.setAttribute("aria-hidden", "true");

    images.forEach((image, imageIndex) => {
      const isActive = imageIndex === activeIndex;
      image.classList.toggle("is-active", isActive);
      image.hidden = !isActive;
      image.setAttribute("aria-hidden", String(!isActive));
    });
    buttons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === activeIndex;
      button.setAttribute("aria-expanded", String(isActive));
      button.toggleAttribute("data-section4-mobile-active", isActive);
    });

    section.dataset.wd031Section4MobileActive = String(activeIndex);
    window.requestAnimationFrame(() => {
      if (activeIndex === index && !desktopMedia.matches) {
        closeButton.focus({ preventScroll: true });
      }
    });
  };

  buttons.forEach((button, index) => {
    const image = images[index];
    button.setAttribute("aria-expanded", "false");
    if (!image) return;

    if (!image.id) image.id = `wd031-section4-mobile-image-${index + 1}`;
    button.setAttribute("aria-controls", image.id);
    button.addEventListener("click", (event) => {
      event.preventDefault();
      openImage(index);
    });
  });

  closeButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeImage();
  });
  mobileStage.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && activeIndex !== null) closeImage();
  });
  desktopMedia.addEventListener("change", () => closeImage(false));

  closeImage(false);
  initializedSection4MobileSections.add(section);
}

function initializeSection4(section) {
  initializeSection4Mobile(section);

  const directImage = getDirectChildByHandle(section, "image");
  const legacyStage = getDirectChildByHandle(section, "container1");
  const stage =
    directImage ||
    (getDirectContainers(legacyStage).length ? legacyStage : section);
  const accordion = Array.from(section.children).find(
    (child) => child.getAttribute("handle")?.toLowerCase() === "container2",
  );

  if (!accordion || initializedSections.has(section)) return;

  const items = getDirectContainers(accordion);
  if (!items.length) return;

  let activeIndex = 0;
  let hasActiveState = false;
  let hotspotController = { setActive: () => {}, animateFor: () => {} };
  let sizeFrame = null;
  const backgroundPreloads = [];
  const cardAnimations = new Map();
  const cardAnimationDuration = 500;
  const reducedMotionMedia = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );

  const getConfiguredAspectRatio = (item) => {
    const value = window
      .getComputedStyle(item)
      .getPropertyValue("--aspect-ratio-pc")
      .trim();
    const [width, height] = value.split("/").map(Number);
    return width > 0 && height > 0 ? width / height : null;
  };

  const syncExpandedHeights = () => {
    sizeFrame = null;
    if (!desktopMedia.matches || !section.isConnected) return;

    items.forEach((item) => {
      const width = item.getBoundingClientRect().width;
      const ratio = getConfiguredAspectRatio(item);
      if (!width || !ratio) return;

      const height = `${width / ratio}px`;
      if (item.style.getPropertyValue("--section4-expanded-height") !== height) {
        item.style.setProperty("--section4-expanded-height", height);
      }
    });
  };

  const scheduleExpandedHeightSync = () => {
    if (sizeFrame !== null) return;
    sizeFrame = window.requestAnimationFrame(syncExpandedHeights);
  };

  items.forEach((item) => {
    const background = window
      .getComputedStyle(item)
      .getPropertyValue("--bg-image-pc")
      .trim();
    const match = background.match(/^url\((['"]?)(.*?)\1\)$/);
    if (!match?.[2]) return;

    const image = new Image();
    image.decoding = "async";
    image.src = match[2];
    if (typeof image.decode === "function") {
      image.decode().catch(() => {});
    }
    backgroundPreloads.push(image);
  });
  section4BackgroundPreloads.set(section, backgroundPreloads);

  const cancelCardAnimations = () => {
    cardAnimations.forEach((animations) => {
      animations.forEach((animation) => animation.cancel());
    });
    cardAnimations.clear();
  };

  const animateCardLayout = (firstRects) => {
    if (!firstRects) return false;

    let didAnimate = false;
    items.forEach((item, index) => {
      const firstRect = firstRects[index];
      const lastRect = item.getBoundingClientRect();
      if (!firstRect?.height || !lastRect.height) return;

      const offsetY = firstRect.top - lastRect.top;
      const scaleY = firstRect.height / lastRect.height;
      if (Math.abs(offsetY) < 0.5 && Math.abs(scaleY - 1) < 0.001) return;

      const animation = item.animate(
        [
          {
            transform: `translate3d(0, ${offsetY}px, 0) scaleY(${scaleY})`,
          },
          { transform: "translate3d(0, 0, 0) scaleY(1)" },
        ],
        {
          duration: cardAnimationDuration,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        },
      );

      didAnimate = true;
      const animations = [animation];
      if (Math.abs(scaleY - 1) >= 0.001) {
        const inverseScaleKeyframes = Array.from({ length: 21 }, (_, step) => {
          const offset = step / 20;
          const currentScale = scaleY + (1 - scaleY) * offset;
          return {
            offset,
            transform: `scaleY(${1 / currentScale})`,
            transformOrigin: "top left",
          };
        });

        Array.from(item.children).forEach((child) => {
          const handle = child.getAttribute("handle")?.toLowerCase();
          if (handle !== "text" && handle !== "richtext") return;

          animations.push(
            child.animate(inverseScaleKeyframes, {
              duration: cardAnimationDuration,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            }),
          );
        });
      }

      cardAnimations.set(item, animations);
      animation.addEventListener(
        "finish",
        () => {
          if (cardAnimations.get(item) === animations) {
            cardAnimations.delete(item);
          }
        },
        { once: true },
      );
    });

    return didAnimate;
  };

  const setActiveItem = (nextIndex) => {
    const maxIndex = items.length - 1;
    const normalizedIndex = Math.max(0, Math.min(nextIndex, maxIndex));
    if (hasActiveState && normalizedIndex === activeIndex) return;

    const shouldAnimate =
      desktopMedia.matches &&
      hasActiveState &&
      !reducedMotionMedia.matches &&
      typeof Element.prototype.animate === "function";
    const firstRects = shouldAnimate
      ? items.map((item) => item.getBoundingClientRect())
      : null;

    cancelCardAnimations();

    activeIndex = normalizedIndex;
    hasActiveState = true;

    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-selected", String(isActive));
      item.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    section.dataset.wideSection4Active = String(activeIndex);
    hotspotController.setActive(activeIndex);
    if (animateCardLayout(firstRects)) {
      hotspotController.animateFor(cardAnimationDuration);
    }
  };

  hotspotController = initializeSection4Hotspots(
    section,
    stage,
    items,
    setActiveItem,
  );

  const syncResponsiveState = () => {
    const isDesktop = desktopMedia.matches;

    if (isDesktop) {
      syncExpandedHeights();
      accordion.classList.add("is-enhanced");
      accordion.setAttribute("role", "tablist");
      items.forEach((item) => item.setAttribute("role", "tab"));
      setActiveItem(activeIndex);
      return;
    }

    cancelCardAnimations();
    accordion.classList.remove("is-enhanced");
    items.forEach((item) => {
      item.classList.remove("is-active");
      item.removeAttribute("aria-selected");
      item.removeAttribute("tabindex");
      item.removeAttribute("role");
    });
    accordion.removeAttribute("role");
    delete section.dataset.wideSection4Active;
    hasActiveState = false;
  };

  items.forEach((item, index) => {
    item.addEventListener("mouseenter", () => {
      if (desktopMedia.matches) setActiveItem(index);
    });
    item.addEventListener("focusin", () => {
      if (desktopMedia.matches) setActiveItem(index);
    });
    item.addEventListener("click", () => {
      if (desktopMedia.matches) setActiveItem(index);
    });
  });

  desktopMedia.addEventListener("change", syncResponsiveState);
  window.addEventListener("resize", scheduleExpandedHeightSync, {
    passive: true,
  });
  syncResponsiveState();
  initializedSections.add(section);
}

function initializeSection7(section) {
  if (initializedSection7s.has(section) || pendingSection7s.has(section)) {
    return;
  }

  const carousel = Array.from(section.children).find(
    (child) =>
      child.hasAttribute("data-embla") &&
      child.getAttribute("handle")?.toLowerCase() === "embla",
  );

  if (!carousel) return;

  pendingSection7s.add(section);
  let attempts = 0;

  const bindCarousel = () => {
    if (!section.isConnected) {
      pendingSection7s.delete(section);
      return;
    }

    const emblaApi = carousel.__tideweEmblaApi;
    if (!emblaApi) {
      attempts += 1;
      if (attempts < 180) {
        window.requestAnimationFrame(bindCarousel);
      } else {
        pendingSection7s.delete(section);
      }
      return;
    }

    const slides =
      typeof emblaApi.slideNodes === "function"
        ? emblaApi.slideNodes()
        : Array.from(carousel.querySelectorAll("[data-embla-slide]"));
    const viewport = carousel.querySelector("[data-embla-viewport]");
    const container = carousel.querySelector("[data-embla-container]");

    if (!slides.length || !viewport || !container) {
      pendingSection7s.delete(section);
      return;
    }

    const getSelectedIndex = () => {
      if (typeof emblaApi.selectedSnap === "function") {
        return emblaApi.selectedSnap();
      }
      if (typeof emblaApi.selectedScrollSnap === "function") {
        return emblaApi.selectedScrollSnap();
      }
      return 0;
    };

    const syncSelectedSlide = () => {
      const selectedIndex = getSelectedIndex();

      slides.forEach((slide, index) => {
        const isSelected = index === selectedIndex;
        slide.classList.toggle("is-selected", isSelected);
        slide.setAttribute("aria-current", isSelected ? "true" : "false");
      });
      section.dataset.wd031Section7Active = String(selectedIndex);
    };

    let visualFrame = null;

    const resetVisualState = () => {
      slides.forEach((slide) => {
        slide.style.removeProperty("--section7-feature-shift");
        slide.style.removeProperty("--section7-feature-width");
        slide.style.removeProperty("--section7-feature-height");
        slide.style.removeProperty("--section7-image-shift");
        slide.style.removeProperty("--section7-feature-opacity");
        slide.style.removeProperty("--section7-image-opacity");
        slide.style.removeProperty("--section7-edge-opacity");
        slide.style.removeProperty("z-index");
      });
    };

    const updateVisualState = () => {
      visualFrame = null;

      if (!desktopMedia.matches || !section.isConnected) {
        resetVisualState();
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const firstSlideRect = slides[0]?.getBoundingClientRect();
      const slideWidth = firstSlideRect?.width ?? 0;
      const slideHeight = firstSlideRect?.height ?? 0;
      const imageHeight =
        slides[0]
          ?.querySelector(":scope > [handle='image' i]")
          ?.getBoundingClientRect().height || slideWidth;
      if (!viewportRect.width || !slideWidth || !slideHeight) return;

      const containerStyle = window.getComputedStyle(container);
      const gap =
        Number.parseFloat(containerStyle.columnGap) ||
        Number.parseFloat(containerStyle.gap) ||
        0;
      const snapDistance = Math.max(1, slideWidth + gap);
      const featureWidth = window.innerWidth * 0.42;
      const maximumShift = Math.max(0, (featureWidth - slideWidth) / 2);
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      const fullyVisibleLimit = Math.max(
        0,
        viewportRect.width / 2 - slideWidth / 2,
      );
      const hiddenLimit = viewportRect.width / 2 + slideWidth / 2;
      const distances = slides.map((slide) => {
        const slideRect = slide.getBoundingClientRect();
        return slideRect.left + slideRect.width / 2 - viewportCenter;
      });

      slides.forEach((slide, index) => {
        const distance = distances[index];
        const normalizedDistance = Math.max(
          -1,
          Math.min(distance / snapDistance, 1),
        );
        const featureOpacity = Math.max(
          0,
          1 - Math.abs(distance) / snapDistance,
        );
        const imageShift = normalizedDistance * maximumShift;
        const featureShift = imageShift;
        const currentFeatureWidth =
          slideWidth + (featureWidth - slideWidth) * featureOpacity;
        const currentFeatureHeight =
          imageHeight + (slideHeight - imageHeight) * featureOpacity;
        const visualDistance = Math.abs(distance + imageShift);
        const edgeVisibility = Math.max(
          0,
          Math.min(
            1,
            1 -
              (visualDistance - fullyVisibleLimit) /
                Math.max(1, hiddenLimit - fullyVisibleLimit),
          ),
        );
        const edgeOpacity =
          edgeVisibility * edgeVisibility * (3 - 2 * edgeVisibility);

        slide.style.setProperty(
          "--section7-feature-shift",
          `${featureShift}px`,
        );
        slide.style.setProperty(
          "--section7-feature-width",
          `${currentFeatureWidth}px`,
        );
        slide.style.setProperty(
          "--section7-feature-height",
          `${currentFeatureHeight}px`,
        );
        slide.style.setProperty(
          "--section7-image-shift",
          `${imageShift}px`,
        );
        slide.style.setProperty(
          "--section7-feature-opacity",
          String(featureOpacity),
        );
        slide.style.setProperty(
          "--section7-image-opacity",
          String(1 - featureOpacity),
        );
        slide.style.setProperty(
          "--section7-edge-opacity",
          String(edgeOpacity),
        );
        slide.style.zIndex = String(Math.round(featureOpacity * 100) + 1);
      });
    };

    const scheduleVisualState = () => {
      if (visualFrame !== null) return;
      visualFrame = window.requestAnimationFrame(updateVisualState);
    };

    const syncCarouselState = () => {
      syncSelectedSlide();
      scheduleVisualState();
    };

    syncCarouselState();
    emblaApi.on("scroll", scheduleVisualState);
    emblaApi.on("select", syncCarouselState);
    emblaApi.on("reinit", syncCarouselState);
    desktopMedia.addEventListener("change", scheduleVisualState);
    window.addEventListener("resize", scheduleVisualState, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(scheduleVisualState)
        : null;
    resizeObserver?.observe(viewport);

    emblaApi.on("destroy", () => {
      if (visualFrame !== null) window.cancelAnimationFrame(visualFrame);
      resizeObserver?.disconnect();
      desktopMedia.removeEventListener("change", scheduleVisualState);
      window.removeEventListener("resize", scheduleVisualState);
      resetVisualState();
    });

    const autoplay = emblaApi.plugins?.().autoplay;
    autoplay?.play?.();
    emblaApi.on("pointerup", () => autoplay?.play?.());

    pendingSection7s.delete(section);
    initializedSection7s.add(section);
  };

  bindCarousel();
}

function initializePage(root = document) {
  const sections = root.matches?.('[handle="section4" i]')
    ? [root]
    : root.querySelectorAll('[handle="section4" i]');

  sections.forEach(initializeSection4);

  const section7s = root.matches?.('[handle="section7" i]')
    ? [root]
    : root.querySelectorAll('[handle="section7" i]');

  section7s.forEach(initializeSection7);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializePage(), {
    once: true,
  });
} else {
  initializePage();
}

document.addEventListener("shopify:section:load", (event) => {
  initializePage(event.target);
});
